/**
 * useVersionCheck
 *
 * Asks the backend every few seconds which frontend build is live. The number
 * in this bundle was baked in at `vite build` time, so a server number that is
 * higher means this tab is running stale code - see shared/appVersion.js.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { FRONTEND_BUILD } from '@shared/appVersion.js';
import { API_BASE_URL } from '../utils/api';
import { reloadToLatestBuild } from '../utils/appReload';

// The ask was 5-10s; 7s keeps it responsive without being a visible stream of
// requests in the network tab
const POLL_INTERVAL_MS = 7000;

// Backend down / deploy in progress - back off instead of hammering it
const FAILURE_INTERVAL_MS = 30000;
const FAILURES_BEFORE_BACKOFF = 3;

// "Later" hides the prompt for this long, then it comes back
const SNOOZE_MS = 10 * 60 * 1000;

// Survives the reload itself, so we can tell "new build available" from
// "already refreshed for this build and the server still says it is newer"
// (i.e. backend bumped but the new frontend was never deployed)
const RELOAD_MARK_KEY = 'easypanel:reloadedForBuild';

const readReloadMark = () => {
  try {
    return parseInt(sessionStorage.getItem(RELOAD_MARK_KEY), 10);
  } catch {
    return NaN;
  }
};

const writeReloadMark = (build) => {
  try {
    sessionStorage.setItem(RELOAD_MARK_KEY, String(build));
  } catch {
    // private mode / storage full - the mark is a nicety, not a requirement
  }
};

export const useVersionCheck = ({ intervalMs = POLL_INTERVAL_MS } = {}) => {
  const [update, setUpdate] = useState(null);
  const [reloading, setReloading] = useState(false);

  const timerRef = useRef(null);
  const failuresRef = useRef(0);
  // Build the user said "Later" to, and until when
  const snoozedBuildRef = useRef(null);
  const snoozedUntilRef = useRef(0);
  // Avoid overlapping polls when one request is slow
  const inFlightRef = useRef(false);

  const checkVersion = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      // Deliberately a bare fetch, not the shared axios client: no auth token
      // (the login screen polls too), no dedup, and - importantly - no
      // X-Update-Activity header, which would otherwise keep an idle open tab
      // looking "active" forever.
      const response = await fetch(`${API_BASE_URL}/version?build=${FRONTEND_BUILD}`, {
        cache: 'no-store',
        credentials: 'omit',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error(`version check failed: ${response.status}`);

      const body = await response.json();
      const serverBuild = Number(body?.data?.build);

      failuresRef.current = 0;

      // `>` not `!==`: running an unbuilt newer frontend against an older
      // backend (normal in local dev) must not nag
      if (!Number.isFinite(serverBuild) || serverBuild <= FRONTEND_BUILD) {
        setUpdate(null);
        return;
      }

      const snoozed =
        snoozedBuildRef.current === serverBuild && Date.now() < snoozedUntilRef.current;
      if (snoozed) return;

      setUpdate({
        build: serverBuild,
        version: body?.data?.version || null,
        releasedAt: body?.data?.releasedAt || null,
        changes: Array.isArray(body?.data?.changes) ? body.data.changes : [],
        currentBuild: FRONTEND_BUILD,
        // Refreshed once for this build and still behind => the new frontend
        // build is not on the server yet, so another refresh will not help
        alreadyReloaded: readReloadMark() === serverBuild,
      });
    } catch {
      // Offline, backend restarting mid-deploy, etc. Never prompt on failure -
      // an unreachable backend says nothing about which build is live.
      failuresRef.current += 1;
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  // Poll on a self-rescheduling timer (not setInterval) so a slow or backed-off
  // request cannot stack up, and a hidden tab does not poll at all
  useEffect(() => {
    let cancelled = false;

    const schedule = (delay) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(run, delay);
    };

    const run = async () => {
      if (cancelled) return;

      if (document.hidden) {
        // Nothing to do while hidden - the visibility handler checks on return
        schedule(intervalMs);
        return;
      }

      await checkVersion();
      if (cancelled) return;

      schedule(
        failuresRef.current >= FAILURES_BEFORE_BACKOFF ? FAILURE_INTERVAL_MS : intervalMs
      );
    };

    const onVisible = () => {
      if (document.hidden) return;
      // Back from a background tab - the most likely moment to be stale
      schedule(0);
    };

    document.addEventListener('visibilitychange', onVisible);
    run();

    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [checkVersion, intervalMs]);

  /**
   * Pull the new build. Marks the attempt first so that, if the refresh comes
   * back still stale, the prompt can say so instead of looping.
   */
  const refresh = useCallback(async () => {
    if (reloading) return;

    setReloading(true);
    if (update?.build) writeReloadMark(update.build);
    await reloadToLatestBuild();
  }, [reloading, update]);

  /**
   * Hide the prompt for SNOOZE_MS. A newer build than the snoozed one prompts
   * again immediately.
   */
  const dismiss = useCallback(() => {
    if (update?.build) {
      snoozedBuildRef.current = update.build;
      snoozedUntilRef.current = Date.now() + SNOOZE_MS;
    }
    setUpdate(null);
  }, [update]);

  return { update, reloading, refresh, dismiss };
};

export default useVersionCheck;
